// 📄 useOrdenCompra.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros
// (ej. destinos RFC, URLs — valores que se tipean directo en cada mandante).
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { savePurchaseOrderConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, PurchaseOrderFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const STEP_KEY_MAP: Record<string, string> = {
  po_output: 'step_output', po_partner: 'step_partner', po_distmodel: 'step_distmodel',
  po_mapping: 'step_mapping', po_test: 'step_test'
};

const DEFAULT_FORM: PurchaseOrderFormData = {
  outputType: '', transmissionMedium: '6', dispatchTime: '4',
  partnerNumber: '', partnerType: 'LS', outboundPort: '',
  distModelName: '', senderLogicalSystem: '', receiverLogicalSystem: '',
  cigProjectId: '', customHeaderFields: false, customItemFields: false,
  testPoNumber: '', transmissionStatus: 'Pendiente',
  requiresOT: false, otNumber: '', comments: ''
};

export function useOrdenCompra() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('po_output');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<PurchaseOrderFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof PurchaseOrderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    const poStepIds = ['po_output', 'po_partner', 'po_distmodel', 'po_mapping', 'po_test'];
    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && poStepIds.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<PurchaseOrderFormData> = {};
    const po = devData.channels?.purchaseOrder;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.purchaseOrder : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (po) {
      if (po.step_output) {
        if (activeStep === 'po_output') {
          const requiresOT = po.step_output.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? po.step_output : (envChannelData?.step_output || {});
          initialFormData.outputType = source.outputType || '';
          initialFormData.transmissionMedium = source.transmissionMedium || '6';
          initialFormData.dispatchTime = source.dispatchTime || '4';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, po_output: po.step_output.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, po_output: po.step_output.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, po_output: po.step_output.customDevelopment || emptyCustomDevelopment() }));
        qasMap.po_output = po.step_output.transport?.QAS || emptyTransportConfirmation();
        prdMap.po_output = po.step_output.transport?.PRD || emptyTransportConfirmation();
      }
      if (po.step_partner) {
        if (activeStep === 'po_partner') {
          const requiresOT = po.step_partner.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? po.step_partner : (envChannelData?.step_partner || {});
          initialFormData.partnerNumber = source.partnerNumber || '';
          initialFormData.partnerType = source.partnerType || 'LS';
          initialFormData.outboundPort = source.outboundPort || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, po_partner: po.step_partner.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, po_partner: po.step_partner.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, po_partner: po.step_partner.customDevelopment || emptyCustomDevelopment() }));
        qasMap.po_partner = po.step_partner.transport?.QAS || emptyTransportConfirmation();
        prdMap.po_partner = po.step_partner.transport?.PRD || emptyTransportConfirmation();
      }
      if (po.step_distmodel) {
        if (activeStep === 'po_distmodel') {
          const requiresOT = po.step_distmodel.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? po.step_distmodel : (envChannelData?.step_distmodel || {});
          initialFormData.distModelName = source.distModelName || '';
          initialFormData.senderLogicalSystem = source.senderLogicalSystem || '';
          initialFormData.receiverLogicalSystem = source.receiverLogicalSystem || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, po_distmodel: po.step_distmodel.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, po_distmodel: po.step_distmodel.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, po_distmodel: po.step_distmodel.customDevelopment || emptyCustomDevelopment() }));
        qasMap.po_distmodel = po.step_distmodel.transport?.QAS || emptyTransportConfirmation();
        prdMap.po_distmodel = po.step_distmodel.transport?.PRD || emptyTransportConfirmation();
      }
      if (po.step_mapping) {
        if (activeStep === 'po_mapping') {
          const requiresOT = po.step_mapping.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? po.step_mapping : (envChannelData?.step_mapping || {});
          initialFormData.cigProjectId = source.cigProjectId || '';
          initialFormData.customHeaderFields = source.customHeaderFields || false;
          initialFormData.customItemFields = source.customItemFields || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, po_mapping: po.step_mapping.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, po_mapping: po.step_mapping.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, po_mapping: po.step_mapping.customDevelopment || emptyCustomDevelopment() }));
        qasMap.po_mapping = po.step_mapping.transport?.QAS || emptyTransportConfirmation();
        prdMap.po_mapping = po.step_mapping.transport?.PRD || emptyTransportConfirmation();
      }
      if (po.step_test) {
        if (activeStep === 'po_test') {
          const requiresOT = po.step_test.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? po.step_test : (envChannelData?.step_test || {});
          initialFormData.testPoNumber = source.testPoNumber || '';
          initialFormData.transmissionStatus = source.transmissionStatus || 'Pendiente';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, po_test: po.step_test.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, po_test: po.step_test.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, po_test: po.step_test.customDevelopment || emptyCustomDevelopment() }));
        qasMap.po_test = po.step_test.transport?.QAS || emptyTransportConfirmation();
        prdMap.po_test = po.step_test.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.purchaseOrder;
    const prdChannelData = configSource?.environments?.PRD?.channels?.purchaseOrder;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    poStepIds.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de PO:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    if (activeStep === 'po_output' && !formData.outputType.trim()) {
      setValidationError("La clase de mensaje (Output Type) es obligatoria."); return;
    }
    if (activeStep === 'po_partner' && (!formData.partnerNumber.trim() || !formData.outboundPort.trim())) {
      setValidationError("El número de interlocutor y puerto de salida son obligatorios."); return;
    }
    if (activeStep === 'po_distmodel' && (!formData.distModelName.trim() || !formData.senderLogicalSystem.trim() || !formData.receiverLogicalSystem.trim())) {
      setValidationError("Todos los campos del modelo de distribución son obligatorios."); return;
    }
    if (activeStep === 'po_mapping' && !formData.cigProjectId.trim()) {
      setValidationError("El ID de proyecto CIG es obligatorio."); return;
    }
    if (activeStep === 'po_test' && !formData.testPoNumber.trim()) {
      setValidationError("El número de orden de compra piloto es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      // Se incluye el transport ya conocido para no perderlo: el guardado
      // de DEV reemplaza el objeto completo del paso en Firestore.
      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await savePurchaseOrderConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'purchaseOrder', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};

      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'purchaseOrder', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange
  };
}
